/**
 * InternalDocDetail.tsx — silnik formatek dokumentów (E4, pilot na klasach
 * gołych — mirror CommercialDocDetail).
 *
 * Jedna strona dla 3 ścieżek:
 *   /internal-docs/new  → tryb create (pusty formularz, edit od razu)
 *   /internal-docs/:id  → tryb view (read-only, przyciski Edytuj/Usuń)
 *   /internal-docs/:id + Edytuj → tryb edit (PATCH, Zapisz/Anuluj)
 *
 * Layout (silnik renderowania, E4 — zastępuje dawny InternalDocDataTab):
 *   `useDocumentRenderField` dostarcza `renderField(fieldId)` dla pól core
 *   (nadpisania: systemNumber/status/urgency/source/ownerUserId/documentTypeId,
 *   patrz `documentFieldConfig.ts`) i deleguje ext-pola do `ExtFieldValueInput`.
 *   `topUpDocumentLayout` syntetyzuje zakładkę `data` z 13 pól core gdy brak
 *   layoutu — jedna ścieżka dla create/edit/view, RecursiveFormRenderer schodzi
 *   rekurencyjnie w children zakładki `data` zamiast `tabRenderers.data`. Pole
 *   dodatkowe pokazuje się tylko tam, gdzie ktoś postawił je w układzie jako
 *   zwykłe pole (top-up niczego nie dokleja).
 *   dokument-blokify (2026-08-24): `audit_log` NIE jest już zaszytą zakładką (top-up
 *   jej nie wymusza) — to przenośny blok palety (internal_audit_view) dispatchowany
 *   przez contextRenderers, usuwalny/dodawalny jak inne bloki. Samodzielnej zakładki
 *   „Pola dodatkowe" (internal_ext_fields_view) już nie ma — pole dodatkowe edytuje się
 *   tylko tam, gdzie zostało postawione w układzie, a jego wartość przychodzi z odpowiedzi
 *   GET rekordu.
 *   contextRenderers → internal_audit_view (przenośny blok, guard !isCreate &&
 *     recordId) + buildDocumentAdditionalRenderers(recordId, { buffered: true, ... })
 *     — tabele dodatkowe (additional_view/satellite_view) dostępne JUŻ w create,
 *     bez HTTP, delty jadą w POST/PATCH.
 *
 * Walidacja (mirror PurchaseInvoiceDetail/Letter): required core
 * (status/urgency/source — enumy z domyślną wartością, walidacja domyka
 * kontrakt) + required ext (`field.isRequired`, z `fields` zwróconych przez
 * `useDocumentRenderField`) blokują `saveMutation.mutate` przed wysyłką.
 * `extractErrorCode` w `onError` mapuje `VALIDATION_ERROR` na dedykowany toast
 * (parytet z fakturami/umowami/pismami), generic fallback pozostaje.
 *
 * Save flow (atomic, mirror Krok 5 — saveAggregate):
 *   - create: POST /internal-docs z pełnym body (pola + extValues +
 *     additionalTables, gdy niepuste) → jeden zapis w jednej transakcji.
 *   - edit:   PATCH /internal-docs/:id analogicznie — jedno wywołanie, bez
 *     osobnego PUT na ext-fields (usunięty; ext-fields i delty tabel
 *     dodatkowych jadą inline w body POST/PATCH, patrz saveAggregate).
 *
 * Moduł: crm_core (ADR-0083; dawniej osobny moduł doc_internal). System-module slug: internal.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { UUID_REGEX } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Pencil, Plus, RotateCcw, Save, Shield, Trash2, X as XIcon } from 'lucide-react';

import { AttachmentsSection } from '@/components/attachments/AttachmentsSection';
import { buildDocumentPreviewContextRenderer } from '@/components/attachments/DocumentPreviewContextRenderer';
import { DocumentPreviewDockHost } from '@/components/attachments/DocumentPreviewDockHost';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  type BareDocFormData,
  EMPTY_BARE_DOC_FORM,
} from '@/components/documents/documentCoreRenderers';
import { DocumentItemsEditor } from '@/components/documents/DocumentItemsEditor';
import { DocumentItemsSection } from '@/components/documents/DocumentItemsSection';
import { DocumentLayoutPicker } from '@/components/documents/DocumentLayoutPicker';
import { DocumentTypeChangeBadge } from '@/components/documents/DocumentTypeChangeBadge';
import { DocumentTypeCreateBadge } from '@/components/documents/DocumentTypeCreateBadge';
import { buildDocumentEngineConfig } from '@/components/documents/engine/documentEngineConfig';
import {
  type TopUpContext,
  topUpDocumentLayout,
} from '@/components/documents/engine/topUpDocumentLayout';
import { useDocumentRenderField } from '@/components/documents/engine/useDocumentRenderField';
import { RecursiveFormRenderer } from '@/components/form/RecursiveFormRenderer';
import { SaveFirstNotice } from '@/components/form/SaveFirstNotice';
import { IconButton } from '@/components/IconButton';
import { buildCoreFieldRecordValues } from '@/components/kartoteki/engine/engine-config';
import type { EngineFormField } from '@/components/kartoteki/engine/engine-types';
import { ModuleGuard } from '@/components/ModuleGuard';
import { ProcessApprovalBanner } from '@/components/processes/ProcessApprovalBanner';
import { NewRecordDefaultsStatus } from '@/components/record-defaults/NewRecordDefaultsStatus';
import { Spinner } from '@/components/Spinner';
import { SystemModuleAuditTab } from '@/components/system-modules/SystemModuleAuditTab';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import { invalidateRecordInstances } from '@/hooks/useActiveInstanceForRecord';
import { useAdditionalTableDeltas } from '@/hooks/useAdditionalTableDeltas';
import { useControlWarning } from '@/hooks/useControlWarning';
import { useDocumentDetailTab } from '@/hooks/useDocumentDetailTab';
import { useDocumentSqlButton } from '@/hooks/useDocumentSqlButton';
import { useDocumentTypeAndLayoutParams } from '@/hooks/useDocumentTypeAndLayoutParams';
import { useFieldErrors } from '@/hooks/useFieldErrors';
import { applyCoreDefaults, useNewRecordDefaults } from '@/hooks/useNewRecordDefaults';
import { useRecordExtValues } from '@/hooks/useRecordExtValues';
import { useSaveBeforeDecision } from '@/hooks/useSaveBeforeDecision';
import { useSystemModuleActiveLayout } from '@/hooks/useSystemModuleActiveLayout';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { buildAdditionalViewRecordValues } from '@/lib/additional-views/record-values';
import { api } from '@/lib/api';
import {
  type DocumentHeaderAmountsFields,
  type DocumentLineItemDraft,
  type DocumentLineItemsFields,
} from '@/lib/documents/document-items';
import {
  draftToPayload,
  serverItemToDraft,
  validateItemsDraft,
} from '@/lib/documents/document-items-draft';
import { standardDocumentStatusLabel } from '@/lib/documents/document-select-labels';
import { useNumberingBaseFieldSlug } from '@/lib/documents/useNumberingBaseFieldSlug';
import { extractErrorCode } from '@/lib/errors';
import { type ProcessWarning, processWarningMessage } from '@/lib/processes/approvals-api';
import { DOCUMENTS_MODULE_CODE } from '@/lib/processes/module-routes';
import { mapDictionarySaveError } from '@/lib/records/save-error-message';
import { useMyModules } from '@/lib/use-my-modules';
import {
  buildExtFieldServerMessages,
  buildFieldIssueMessages,
  buildMissingFieldsDescription,
  buildValidationToastMessage,
  resolveDocumentFieldLabel,
} from '@/lib/validation-toast';

import { buildDocumentAdditionalRenderers } from './document-additional-renderers';

/**
 * Config silnika klasy gołej — tu tylko jako źródło mapy slug → pole formularza dla wartości
 * nagłówka widocznych w widokach i tabelach dodatkowych (`buildCoreFieldRecordValues`).
 */
const internalEngineConfig = buildDocumentEngineConfig('internal');

interface InternalDocDetail
  extends BareDocFormData, DocumentLineItemsFields, DocumentHeaderAmountsFields {
  id: string;
  companyId: string;
  gcrecord: string | null;
  createdAt: string;
  updatedAt: string;
  /** Surowe wartości pól dodatkowych — dołączone przez serwer do odpowiedzi GET rekordu. */
  extValues?: Record<string, unknown>;
}

interface GetResponse {
  internalDoc: InternalDocDetail;
}

/**
 * Wymagane pola przed zapisem (mirror PurchaseInvoiceDetail §5.4 planu silnika):
 * core status/urgency/source (twarde enumy z domyślną wartością — realnie
 * zawsze wypełnione, walidacja domyka kontrakt) + ext-pola oznaczone
 * `isRequired` w `fields` zwróconych przez `useDocumentRenderField`.
 */
function collectMissingRequired(
  form: BareDocFormData,
  extValues: Record<string, unknown>,
  fields: EngineFormField[],
): Set<string> {
  const missing = new Set<string>();
  if (!form.status) missing.add('status');
  if (!form.urgency) missing.add('urgency');
  if (!form.source) missing.add('source');
  for (const field of fields) {
    if (field.isCore || field.gcrecord !== null || !field.isRequired) continue;
    const value = extValues[field.slug];
    if (value == null || (typeof value === 'string' && value.trim() === '')) {
      missing.add(field.slug);
    }
  }
  return missing;
}

export function InternalDocDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id?: string }>();
  const isCreate = !id || id === 'new';
  const { data: myModules } = useMyModules();
  const processesActive = myModules?.subscriptions.some((s) => s.code === 'processes') ?? false;
  const navigate = useNavigate();
  // Powrót na listę niesie jej kontekst — patrz useWithMenuItem.
  const withMenuItem = useWithMenuItem();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { me } = useTenantAuth();
  // Podświetlenie pola odrzuconego przez serwer (VALIDATION_ERROR.details) — boolean-only,
  // komunikat zostaje w toaście (patrz onError niżej i hasError w useDocumentRenderField).
  const fe = useFieldErrors();

  // /api/auth/me zwraca dziś puste `permissions` (wypełnienie odroczone) — pusta lista
  // oznacza „brak wiedzy", nie „brak uprawnień": pokazujemy akcje, backend egzekwuje.
  const permissionsUnknown = !!me && me.permissions.length === 0;

  const canEdit =
    me?.user.isSuperUser === true ||
    permissionsUnknown ||
    me?.permissions.includes('documents.internal.edit') === true;

  const canDelete =
    me?.user.isSuperUser === true ||
    permissionsUnknown ||
    me?.permissions.includes('documents.internal.delete') === true;

  const [searchParams, setSearchParams] = useSearchParams();
  const { tab, setTab } = useDocumentDetailTab();

  const [editing, setEditing] = useState(isCreate);
  // Rework D.8 — typ wybrany w edycji, niezapisany do czasu głównego zapisu; reset
  // przy zmianie rekordu (mirror layoutOverrideId w useDocumentTypeAndLayoutParams).
  const [pendingDocumentTypeId, setPendingDocumentTypeId] = useState<string | null>(null);
  useEffect(() => {
    setPendingDocumentTypeId(null);
  }, [id]);
  const [form, setForm] = useState<BareDocFormData>(() => ({
    ...EMPTY_BARE_DOC_FORM,
    documentTypeId: isCreate ? (searchParams.get('documentTypeId') ?? null) : null,
  }));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(false);

  // Draft edytowalnych pozycji — inicjalizowany z odpowiedzi serwera (efekt niżej),
  // wysyłany jako tablica `items` w tym samym zapisie co dokument. `itemsDirty`
  // steruje semantyką API: wysyłamy `items` TYLKO gdy użytkownik ruszył grid (inaczej
  // pomijamy → backend nie tyka pozycji; bez zbędnego replace-setu przy edycji nagłówka).
  const [itemsDraft, setItemsDraft] = useState<DocumentLineItemDraft[]>([]);
  const [itemsDirty, setItemsDirty] = useState(false);
  // Klucze wierszy niekompletnych z ostatniej próby zapisu (patrz handleSave) —
  // podświetlenie w edytorze, zerowane przy kolejnej udanej walidacji.
  const [invalidItemRowKeys, setInvalidItemRowKeys] = useState<Set<string>>(new Set());
  const handleItemsChange = (next: DocumentLineItemDraft[]) => {
    setItemsDraft(next);
    setItemsDirty(true);
  };

  // Atomic-save refactor (mirror ContractDetail/SaleInvoiceDetail): bufor delt
  // tabel dodatkowych — jeden wpis per tableId, agregowany z każdej instancji
  // <AdditionalTableTab buffered> na tej stronie. Wyciągnięte do współdzielonego
  // hooka (Etap 2.2, unifikacja-formatek) — pełny kontrakt i uzasadnienie
  // freshness `getBufferedValue` w nagłówku `useAdditionalTableDeltas.ts`.
  const {
    handleAdditionalDeltasChange,
    getBufferedValue,
    flattenAdditionalDeltas,
    buildAdditionalParentValues,
    resetAdditionalDeltas,
    getAddRowDraft,
    handleAddRowDraftChange,
    resetAddRowDrafts,
  } = useAdditionalTableDeltas();

  const detailQ = useQuery({
    queryKey: ['internal-doc', id],
    queryFn: async () => {
      const res = await api.get<GetResponse>(`/internal-docs/${id}`);
      return res.data.internalDoc;
    },
    enabled: !isCreate && !!id,
  });

  // Wartości domyślne pól (Ustawienia kartoteki → Pola) dla formularza nowego dokumentu — pola
  // pokazujemy dopiero po ich wczytaniu (patrz `NewRecordDefaultsStatus` niżej).
  const recordDefaults = useNewRecordDefaults('internal', isCreate);

  // Ext-fields (pola dodatkowe): lokalny stan zasilany wartością z odpowiedzi GET rekordu
  // (`detailQ.data.extValues`, bez osobnego żądania) — pełny kontrakt synchronizacji
  // w nagłówku `useRecordExtValues.ts`. Przy tworzeniu „wartością serwera” są wartości
  // domyślne pól dodatkowych.
  const { extValues, handleExtChange, markSaved, snapshotEdits, discardEdits } = useRecordExtValues(
    {
      recordId: isCreate ? undefined : id,
      serverExtValues: isCreate ? recordDefaults.ext : detailQ.data?.extValues,
      serverVersion: detailQ.dataUpdatedAt,
    },
  );

  // T1.6 (layouty-e1): typ dokumentu do rozstrzygnięcia layoutu — duplikat
  // (walidacja UUID) walidacji `effectiveDocumentTypeId` z
  // `useDocumentTypeAndLayoutParams` niżej — niemożliwe do uniknięcia:
  // ten hook aktywnego layoutu MUSI zostać na stronie (kontrakt Etapu 2.3) i
  // potrzebuje typu PRZED wywołaniem `useDocumentTypeAndLayoutParams` (który
  // z kolei przyjmuje `layoutConfig` STĄD jako `activeLayoutConfig`). Świadomie
  // NIE gate'ujemy hooka poniżej `enabled: false` do czasu załadowania detalu
  // (patrz komentarz w ContractDetail.tsx) — jeden dodatkowy fetch bez typu +
  // refetch z typem po jego przyjściu, zero ryzyka przedwczesnego renderu fallbacku.
  const rawCreateDocumentTypeIdForLayout = isCreate ? searchParams.get('documentTypeId') : null;
  const documentTypeIdForLayout = isCreate
    ? rawCreateDocumentTypeIdForLayout && UUID_REGEX.test(rawCreateDocumentTypeIdForLayout)
      ? rawCreateDocumentTypeIdForLayout
      : null
    : (pendingDocumentTypeId ?? detailQ.data?.documentTypeId ?? null);
  const { layoutConfig, myRoleId, isSuperUser, previewDockEnabled } = useSystemModuleActiveLayout(
    'internal',
    {
      documentTypeId: documentTypeIdForLayout,
    },
  );

  // Etap 2.3 (unifikacja-formatek): create-params / layout-override cluster
  // wyciągnięty do współdzielonego hooka — pełny kontrakt i uzasadnienie
  // (redirect guard, walidacja UUID, reset override przy zmianie rekordu) w
  // nagłówku `useDocumentTypeAndLayoutParams.ts`.
  const {
    createCompanyId,
    missingDocumentTypeId,
    effectiveDocumentTypeId,
    layoutOverrideId,
    setLayoutOverrideId,
    effectiveLayoutConfig,
  } = useDocumentTypeAndLayoutParams({
    isCreate,
    searchParams,
    navigate,
    listRoute: withMenuItem('/internal-docs'),
    moduleSlug: 'internal',
    recordId: id ?? null,
    detailDocumentTypeId: documentTypeIdForLayout,
    activeLayoutConfig: layoutConfig,
  });

  // Silnik formatek (E4) — renderField(fieldId) dla pól core (nadpisania
  // per-slug, patrz documentFieldConfig.ts) i delegacja ext-pól do
  // ExtFieldValueInput. `fields` reużyty niżej w walidacji required. T3 (owner
  // scoping): `companyId` przekazany do rendererów (owner_user_id → UserPicker
  // scoped po firmie) — create używa firmy z kreatora typu dokumentu, edit
  // firmy zapisanej na rekordzie (przesunięte NIŻEJ niż `detailQ`/
  // `createCompanyId`, żeby obie wartości były już dostępne w tym miejscu —
  // reorder hooków bez zmiany semantyki, zero warunkowych wywołań).
  // Gwiazdka „wymagane" przy polu daty numeracji wybranego typu (patrz PurchaseInvoiceDetail).
  const numberingBaseFieldSlug = useNumberingBaseFieldSlug(
    'internal',
    effectiveDocumentTypeId,
    createCompanyId ?? detailQ.data?.companyId ?? null,
  );

  const { renderField, fields } = useDocumentRenderField({
    docClass: 'internal',
    form,
    onChange: setForm,
    extValues,
    onExtChange: handleExtChange,
    editable: editing,
    isCreate,
    companyId: createCompanyId ?? detailQ.data?.companyId ?? null,
    numberingBaseFieldSlug,
    hasError: (key) => !!fe.get(String(key)),
    getError: (key) => fe.get(String(key)),
    clearError: (key) => fe.clear(String(key)),
  });

  // Zakładki strony dosypywane przez top-up (dedup po slugu, §4.1.e planu
  // silnika) — 'additional' NIE jest wstrzykiwana: tabele dodatkowe mają
  // własne sloty/tytuły z buildera (per firma), top-up by je zdublował.
  const topUpCtx = useMemo<TopUpContext>(
    () => ({
      // dokument-blokify (2026-08-24): 'ext_fields'/'audit_log' USUNIĘTE z pageTabs —
      // top-up dosypywał je bezwarunkowo przy każdym renderze (nawet po świadomym usunięciu
      // w builderze), co łamało portability. Teraz blok audytu jest zasiany jako NORMALNE
      // dziecko w domyślnym (seedowanym) layoucie `internal` (mirror kartotek); pól
      // dodatkowych top-up ani seed nie dokleja — są tylko tam, gdzie stoją w układzie.
      pageTabs: [{ slug: 'data', title: t('tenant.documents.shared.tabs.data'), sortOrder: 0 }],
      coreFieldSlugs: [
        'system_number',
        'title',
        'status',
        'document_date',
        'description',
        'urgency',
        'owner_user_id',
        'category_id',
        'notes',
        'rejection_reason',
        'source',
        'external_id',
        'external_source',
      ],
    }),
    [t],
  );

  // Top-up (E4, §4.1.d planu silnika) — czysta funkcja stosowana do EFEKTYWNEGO
  // layoutu (pokrywa active i sesyjny override jednym wywołaniem), zawsze
  // zwraca znormalizowany kształt z zakładką 'data' (pozostałe zakładki niesie układ).
  const engineLayout = useMemo(
    () => topUpDocumentLayout(effectiveLayoutConfig, topUpCtx),
    [effectiveLayoutConfig, topUpCtx],
  );

  // Sync server → local form. W trybie create formularz startuje z wartości domyślnych pól
  // (nadpisują wartości startowe z kodu); documentTypeId z URL nakładamy PO nich.
  useEffect(() => {
    if (isCreate) {
      setForm({
        ...applyCoreDefaults(EMPTY_BARE_DOC_FORM, recordDefaults.core),
        documentTypeId: searchParams.get('documentTypeId') ?? null,
      });
      return;
    }
    // Rework D.8 — nie nadpisuj niezapisanych zmian w trakcie edycji: odświeżenie
    // detalu w tle (inna mutacja/zakładka unieważniająca ten klucz) nie może cofnąć
    // edytowanego formularza. Po zapisie efekt dogania świeży stan serwera, bo
    // `editing` wraca na false w tym samym onSuccess co invalidate.
    if (editing) return;
    if (detailQ.data) {
      const d = detailQ.data;
      setForm({
        systemNumber: d.systemNumber,
        title: d.title,
        status: d.status,
        urgency: d.urgency,
        description: d.description,
        notes: d.notes,
        documentDate: d.documentDate,
        source: d.source,
        externalId: d.externalId,
        externalSource: d.externalSource,
        rejectionReason: d.rejectionReason,
        ownerUserId: d.ownerUserId ?? null,
        categoryId: d.categoryId ?? null,
      });
    }
    // documentTypeId czytamy wyłącznie przy pierwszym mount'cie / SPA-nawigacji na /new;
    // dołączenie searchParams powodowałoby reset formularza przy każdej zmianie taba.
    // `recordDefaults.core` zmienia referencję raz (wczytanie) — pola są wtedy jeszcze ukryte.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchParams świadomie pominięty (patrz wyżej)
  }, [detailQ.data, isCreate, editing, recordDefaults.core]);

  // Sync server → draft pozycji (przy załadowaniu detalu i po zapisie). Gdy użytkownik
  // ma lokalne zmiany (`itemsDirty`) — draft zostaje nietknięty. Po zapisie onSuccess
  // zeruje `itemsDirty`, więc ten efekt resynchronizuje ze świeżym stanem serwera.
  useEffect(() => {
    if (itemsDirty) return;
    setItemsDraft((detailQ.data?.items ?? []).map(serverItemToDraft));
  }, [detailQ.data?.items, itemsDirty]);

  // Klik decyzji w banerze procesu, gdy formularz jest w edycji: najpierw ten sam zapis co
  // „Zapisz", okno decyzji dopiero po jego sukcesie (useSaveBeforeDecision; „zapis już trwa"
  // sprawdza sam hak po kluczu `saveMutationKey` mutacji zapisu tej kopii formularza).
  // `succeeded` / `aborted` są wołane z `saveMutation.onSuccess/onError` i z anulowania
  // ostrzeżenia kontroli niżej.
  const saveBeforeDecision = useSaveBeforeDecision({
    editing,
    recordId: id,
    save: handleSave,
  });

  // Przycisk SQL (klocek `sql_button`): zadania w tle na tym dokumencie — stan, pasek blokady, renderer
  // klocka, utworzenie → zadanie → przejście (patrz nagłówek `useDocumentSqlButton`).
  const sqlButton = useDocumentSqlButton({
    recordClass: 'internal',
    isCreate,
    recordId: id,
    layoutConfig: effectiveLayoutConfig,
    save: handleSave,
    createCompanyId,
    saveBeforeDecision,
  });

  // Kontrola dokumentu (F20+ reużywalny hook, patrz useControlWarning) — `mutate`
  // celowo owinięty w lambdę: `saveMutation` deklarowana niżej, ale to wywołanie
  // trafia dopiero do zamknięcia `handleConfirm`, uruchamianego po pełnym mount'cie.
  const controlWarning = useControlWarning<BareDocFormData & { warningsAccepted?: boolean }>({
    mutate: (payload) => saveMutation.mutate(payload),
    // Anulowane ostrzeżenie kontroli = zapis się nie odbył → okno decyzji się nie otwiera, a zadanie
    // przycisku SQL kliknięte na nowym dokumencie nie rusza.
    onCancel: sqlButton.onSaveAborted,
  });

  const saveMutation = useMutation({
    mutationKey: saveBeforeDecision.saveMutationKey,
    mutationFn: async (data: BareDocFormData & { warningsAccepted?: boolean }) => {
      const cleaned: Record<string, unknown> = {
        ...(data.title ? { title: data.title } : {}),
        status: data.status,
        urgency: data.urgency,
        description: data.description || null,
        notes: data.notes || null,
        documentDate: data.documentDate || null,
        source: data.source,
        ownerUserId: data.ownerUserId || null,
        categoryId: data.categoryId ?? null,
        externalId: data.externalId || null,
        externalSource: data.externalSource || null,
        rejectionReason: data.rejectionReason || null,
      };

      // Rework D.8 — typ dokumentu wchodzi do payloadu przy tworzeniu ZAWSZE, a przy
      // edycji TYLKO gdy user faktycznie wybrał inny typ (pendingDocumentTypeId). Bez
      // warunku edycja wysyłałaby dziś `null` (schema PATCH: documentTypeId optional,
      // NIE nullable) i dostawała 400.
      if (isCreate) {
        cleaned.documentTypeId = data.documentTypeId || null;
      } else if (
        pendingDocumentTypeId !== null &&
        pendingDocumentTypeId !== detailQ.data?.documentTypeId
      ) {
        cleaned.documentTypeId = pendingDocumentTypeId;
      }

      // Kontrola dokumentu (tenant hook `document_control`) — ostrzeżenie miękkie
      // ponawiane z tą flagą po potwierdzeniu w dialogu (patrz useControlWarning).
      if (data.warningsAccepted) {
        cleaned.warningsAccepted = true;
      }

      // Atomic-save refactor (mirror ContractDetail/SaleInvoiceDetail): jeden
      // zapis — extValues + delty tabel dodatkowych jadą w TYM SAMYM body co
      // pola dokumentu (backend zapisuje wszystko w jednej transakcji, patrz
      // saveAggregate). Pola opcjonalne — pomijamy gdy puste, zachowanie
      // identyczne jak dotąd dla callerów bez nich.
      // Migawka edycji tuż przed włożeniem `extValues` do żądania — PRZED oczekiwaniem na
      // definicje pól tabel (`buildAdditionalParentValues`): edycja zrobiona w tym czasie nie
      // trafia do body, więc `markSaved()` w onSuccess jej nie zdejmie (nagłówek `useRecordExtValues.ts`).
      snapshotEdits();
      if (Object.keys(extValues).length > 0) {
        cleaned.extValues = extValues;
      }
      const additionalTablesBody = flattenAdditionalDeltas();
      if (additionalTablesBody) {
        cleaned.additionalTables = additionalTablesBody;
        // Wartości pól dokumentu z ekranu dla wiązań `parent_field` w wierszach tabel dodatkowych
        // — OBOK delt, liczone ze stanu przekazanego do zapisu (`data`), tylko skalary.
        const additionalParentValues = await buildAdditionalParentValues(
          buildAdditionalViewRecordValues(
            extValues,
            buildCoreFieldRecordValues(internalEngineConfig, data),
          ),
          'internal',
        );
        if (additionalParentValues) {
          cleaned.additionalParentValues = additionalParentValues;
        }
      }

      // Increment 2: pozycje jadą w TYM SAMYM body (replace-set; kwoty pochodne i
      // amount_* przelicza silnik serwera w tej samej transakcji). Wysyłamy TYLKO gdy
      // grid ruszony — inaczej pomijamy `items` (backend nie tyka pozycji). Pusty draft
      // po ruszeniu = `[]` = kasacja pozycji + powrót do trybu kwot ręcznych.
      if (itemsDirty) {
        cleaned.items = itemsDraft.map(draftToPayload);
      }

      let recordId: string;
      let processWarning: ProcessWarning | undefined;
      if (isCreate) {
        // CORE INVARIANT: w trybie „Wszystkie firmy" interceptor NIE wysyła
        // X-Company-Id (brak aktywnej firmy) — bez jawnego nagłówka backend
        // (`requireCompany`) odrzuciłby POST z MISSING_HEADER, nawet gdy
        // documentTypeId jednoznacznie wskazuje firmę. Jawny nagłówek per-request
        // wygrywa z interceptorem (patrz `lib/api.ts` — nie nadpisuje już ustawionego
        // X-Company-Id) i działa identycznie w trybie jednej aktywnej firmy.
        const res = await api.post<{
          id: string;
          systemNumber: string | null;
          processWarning?: ProcessWarning;
        }>(
          '/internal-docs',
          cleaned,
          createCompanyId ? { headers: { 'X-Company-Id': createCompanyId } } : undefined,
        );
        recordId = res.data.id;
        processWarning = res.data.processWarning;
      } else {
        await api.patch(`/internal-docs/${id}`, cleaned);
        recordId = id!;
      }

      return { id: recordId, processWarning };
    },
    onSuccess: async (data) => {
      // Udany zapis gasi podświetlenia z poprzedniej nieudanej próby (ramka + tekst).
      fe.clearAll();
      void queryClient.invalidateQueries({ queryKey: ['internal-docs'] });
      // Zapis mógł zmienić typ dokumentu (a od niego zależą prawa obiegu): flagi „Wyślij do
      // akceptacji" / „Anuluj proces" odświeżamy RAZEM z rekordem (bez czekania na nie).
      void invalidateRecordInstances(queryClient, DOCUMENTS_MODULE_CODE, data.id);
      // Awaitowane: bez tego setItemsDirty(false)/setEditing(false) (niżej) flipowały
      // stan PRZED odświeżeniem detalu, co na chwilę pokazywało stare pozycje przed
      // resyncem draftu (post-save stale-items flash). Reszta invalidacji zostaje
      // fire-and-forget — tylko ta jedna gatekeeperuje UI po zapisie pozycji.
      // Pola dodatkowe zapisane razem z dokumentem — od tej chwili wartość serwera (z odświeżonego
      // detalu) może je nadpisać; bez tego guard niezapisanych edycji trzymałby stare wartości.
      markSaved();
      await queryClient.invalidateQueries({ queryKey: ['internal-doc', data.id] });
      // Atomic-save refactor (mirror ContractDetail/SaleInvoiceDetail): po
      // zapisie odśwież rekordy tabel dodatkowych (prefix match — łapie
      // wszystkie ['additional-records', tableId, parentId]) i wyczyść lokalny
      // bufor delt — backend już je zapisał atomowo.
      void queryClient.invalidateQueries({ queryKey: ['additional-records'] });
      resetAdditionalDeltas();
      // Finding [G] (pre-commit-verify): szkic niezatwierdzonego add-row też czyścimy
      // po udanym zapisie — spójnie z buforem zatwierdzonych wierszy powyżej.
      resetAddRowDrafts();
      // Pozycje zapisane atomowo — zeruj dirty, żeby efekt sync odświeżył draft ze
      // świeżego stanu serwera po refetchu detalu.
      setItemsDirty(false);
      if (isCreate) {
        toast.show({
          variant: 'success',
          title: t('tenant.documents.internal.detail.toast.created'),
        });
        if (data.processWarning) {
          toast.show({
            variant: 'warning',
            title: processWarningMessage(t, data.processWarning),
          });
        }
        // Przycisk SQL kliknięty na nowym dokumencie: zadanie rusza TERAZ — z id nowego dokumentu, PRZED
        // przejściem pod jego adres. Porażka zlecenia dostaje własny komunikat; dokument już istnieje,
        // więc przejście i tak następuje.
        await sqlButton.runPendingCreateRun(data.id);
        // Karta „nowy" ma zamienić się w kartę zapisanego rekordu (replaceTab) —
        // reset do podglądu, bo create→detail nie remontuje strony.
        setEditing(false);
        navigate(withMenuItem(`/internal-docs/${data.id}`), { state: { replaceTab: true } });
        return;
      }
      toast.show({
        variant: 'success',
        title: t('tenant.documents.internal.detail.toast.updated'),
      });
      setPendingDocumentTypeId(null);
      setEditing(false);
      // Zapis wywołany kliknięciem decyzji w banerze → teraz otwórz okno decyzji (no-op, gdy
      // zapis poszedł zwykłym „Zapisz"). Tylko gałąź edycji — przy tworzeniu nie ma banera.
      saveBeforeDecision.succeeded();
    },
    onError: (err) => {
      // Zapis wywołany kliknięciem decyzji w banerze: każdy błąd poza ostrzeżeniem kontroli
      // kończy sprawę (okno decyzji się nie otworzy). O `CONTROL_WARNING` rozstrzyga user w
      // dialogu: potwierdzenie ponawia zapis (okno otworzy jego sukces), anulowanie woła
      // `onCancel`. Na samym początku, żeby nic z dalszej obsługi błędu (podświetlenia pól,
      // wczesne `return`) nie ominęło tego zwolnienia.
      const code = extractErrorCode(err);
      // To samo dla zadania przycisku SQL klikniętego na nowym dokumencie: utworzenie się nie udało → nie rusza.
      if (code !== 'CONTROL_WARNING') sqlButton.onSaveAborted();
      // Podświetlenie + tekst pod polem odrzuconym przez serwer musi zobaczyć KAŻDĄ
      // odpowiedź, zanim jakakolwiek gałąź zrobi wczesny `return` (patrz JSDoc
      // `useFieldErrors.ts`). Kolejność: (1) polski z issue Zoda, (2) własny komunikat
      // serwera z `details` (reguły krzyżowe), (3) generyk. Angielskie default-y Zoda
      // nie przeciekają (pola z issue mają wpis w fieldMsgs i wygrywają przed messages[0]).
      const fieldMsgs = buildFieldIssueMessages(err, t);
      fe.applyServerError(err, {
        translate: (field, messages) =>
          fieldMsgs[field] ?? messages[0] ?? t('common.validationIssue.generic'),
      });
      // Kontrola dokumentu (CONTROL_ERROR/CONTROL_WARNING/CONTROL_HOOK_FAILED) ma
      // pierwszeństwo — jeśli obsłużona (toast lub dialog już pokazany), reszta
      // gałęzi (mapowanie kodów domenowych, mirror Letter/PI/SI/Contract) nie
      // musi się odpalać.
      if (controlWarning.handleError(err, form)) return;
      // Zapis odrzucony, bo na dokumencie trwa zadanie przycisku SQL — czytelny komunikat z opisem zadania
      // zamiast ogólnego „nie udało się zapisać”; odświeżony stan zadań pokaże pasek blokady.
      if (sqlButton.handleLockedError(err)) return;
      if (code === 'VALIDATION_ERROR') {
        const specific = buildValidationToastMessage(
          err,
          (field) => {
            const key = `tenant.documents.shared.fields.${field}`;
            return i18n.exists(key) ? t(key) : null;
          },
          t,
        );
        toast.show({
          variant: 'error',
          title:
            specific ??
            t('tenant.documents.internal.detail.toast.validationError', {
              defaultValue: 'Sprawdź wymagane pola',
            }),
          // Brak wymaganego pola dodatkowego serwer zgłasza w `details` (bez `issues`), więc
          // `specific` jest wtedy puste — nazwij brakujące pola komunikatem serwera.
          description: specific ? undefined : buildExtFieldServerMessages(err, fields),
        });
        return;
      }
      // Odrzucona część słownikowa zapisu (pozycja spoza słownika / brak lub zła wartość
      // parametru słownika) — czytelna przyczyna zamiast ogólnego „błąd zapisu".
      const dictionaryMsg = mapDictionarySaveError(err, t);
      if (dictionaryMsg !== null) {
        toast.show({ variant: 'error', title: dictionaryMsg });
        return;
      }
      toast.show({
        variant: 'error',
        title: t('tenant.documents.internal.detail.toast.saveError'),
      });
    },
  });

  // Walidacja klienta przed wysyłką (mirror PurchaseInvoiceDetail): brakujące
  // wymagane pola blokują zapis i pokazują toast zamiast czekać na odrzucenie
  // 400 z backendu. `fields` (z useDocumentRenderField) niosą `isRequired` dla
  // ext-pól; core (status/urgency/source) sprawdzane wprost (collectMissingRequired).
  // Zwraca, czy zapis wystartował (`false` = zatrzymała go walidacja) — z tego korzysta
  // `useSaveBeforeDecision` (klik decyzji w banerze w edycji = najpierw ten sam zapis).
  function handleSave(): boolean {
    const missing = collectMissingRequired(form, extValues, fields);
    if (missing.size > 0) {
      toast.show({
        variant: 'error',
        title: t('tenant.documents.internal.detail.toast.validationError', {
          defaultValue: 'Sprawdź wymagane pola',
        }),
        // Etykiety jak na formularzu: pole dodatkowe = nazwa z katalogu, rdzeniowe = i18n renderera.
        description: buildMissingFieldsDescription(
          missing,
          (key) => resolveDocumentFieldLabel(fields, key, t, (k) => i18n.exists(k)),
          t,
        ),
      });
      return false;
    }

    // Grid pozycji ruszony — zablokuj zapis, gdy któryś wiersz nie ma kompletu pól
    // wymaganych przez backend (patrz `validateItemsDraft`), zamiast wysyłać
    // niekompletny wiersz i pokazywać dopiero odpowiedź 400 serwera.
    if (itemsDirty && itemsDraft.length > 0) {
      const invalidRows = validateItemsDraft(itemsDraft);
      if (invalidRows.size > 0) {
        setInvalidItemRowKeys(invalidRows);
        toast.show({
          variant: 'error',
          title: t('tenant.documents.items.editor.rowsIncomplete'),
        });
        return false;
      }
    }
    setInvalidItemRowKeys(new Set());

    saveMutation.mutate(form);
    return true;
  }

  // Kontrola dokumentu — flow DELETE (osobna instancja hooka: własne klucze i18n
  // "Usuń mimo to" zamiast "Zapisz mimo to", patrz useControlWarning).
  const deleteControlWarning = useControlWarning<{ warningsAccepted?: boolean }>({
    mutate: (payload) => deleteMutation.mutate(payload),
    titleKey: 'tenant.documents.controlWarning.deleteTitle',
    confirmLabelKey: 'tenant.documents.controlWarning.deleteConfirmLabel',
  });

  const deleteMutation = useMutation({
    mutationFn: async (vars: { warningsAccepted?: boolean }) => {
      const query = vars.warningsAccepted ? '?warningsAccepted=true' : '';
      await api.delete(`/internal-docs/${id}${query}`);
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.documents.internal.detail.toast.deleted'),
      });
      queryClient.invalidateQueries({ queryKey: ['internal-docs'] });
      navigate(withMenuItem('/internal-docs'));
    },
    onError: (err) => {
      // Kontrola dokumentu — jeśli obsłużona (toast lub dialog już pokazany),
      // generyczny toast poniżej nie musi się odpalać.
      if (deleteControlWarning.handleError(err, {})) return;
      // Usuwanie odrzucone na czas zadania przycisku SQL — jak przy zapisie (opis zadania + pasek blokady).
      if (sqlButton.handleLockedError(err)) return;
      toast.show({
        variant: 'error',
        title: t('tenant.documents.internal.detail.toast.deleteError'),
      });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/internal-docs/${id}/restore`, {});
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.documents.internal.detail.toast.restored'),
      });
      void queryClient.invalidateQueries({ queryKey: ['internal-docs'] });
      void queryClient.invalidateQueries({ queryKey: ['internal-doc', id] });
    },
    onError: (err) => {
      // Przywrócenie odrzucone na czas zadania przycisku SQL — opis zadania + pasek blokady.
      if (sqlButton.handleLockedError(err)) return;
      toast.show({
        variant: 'error',
        title: t('tenant.documents.internal.detail.toast.restoreError'),
      });
    },
  });

  function handleCancel() {
    fe.clearAll();
    setPendingDocumentTypeId(null);
    if (isCreate) {
      navigate(withMenuItem('/internal-docs'));
      return;
    }
    if (detailQ.data) {
      const d = detailQ.data;
      setForm({
        systemNumber: d.systemNumber,
        title: d.title,
        status: d.status,
        urgency: d.urgency,
        description: d.description,
        notes: d.notes,
        documentDate: d.documentDate,
        source: d.source,
        externalId: d.externalId,
        externalSource: d.externalSource,
        rejectionReason: d.rejectionReason,
        ownerUserId: d.ownerUserId ?? null,
        categoryId: d.categoryId ?? null,
      });
    }
    discardEdits();
    // Anuluj porzuca też zbuforowane zmiany tabel dodatkowych i szkice add-row (nie tylko zapis).
    resetAdditionalDeltas();
    resetAddRowDrafts();
    setEditing(false);
  }

  // Redirect w toku (obsługiwany w useDocumentTypeAndLayoutParams) — nie renderuj formularza create
  // ani na moment, żeby user nie zobaczył migawki formularza bez typu dokumentu.
  if (missingDocumentTypeId) {
    return (
      <div
        className="flex items-center justify-center p-12"
        data-testid="internal-doc-detail-loading"
      >
        <Spinner />
      </div>
    );
  }

  if (!isCreate && detailQ.isLoading) {
    return (
      <div
        className="flex items-center justify-center p-12"
        data-testid="internal-doc-detail-loading"
      >
        <Spinner />
      </div>
    );
  }

  if (!isCreate && detailQ.isError) {
    return (
      <div className="space-y-4 p-6">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ArrowLeft className="h-4 w-4" aria-hidden="true" />}
          onClick={() => navigate(withMenuItem('/internal-docs'))}
        >
          {t('tenant.documents.internal.detail.backToList')}
        </Button>
        <div className="rounded-xl border border-status-error-border bg-status-error-bg p-6 text-sm text-status-error">
          {t('tenant.documents.internal.detail.notFound')}
        </div>
      </div>
    );
  }

  if (isCreate && recordDefaults.status !== 'ready') {
    return <NewRecordDefaultsStatus defaults={recordDefaults} testIdPrefix="internal-doc-detail" />;
  }

  const recordId = isCreate ? null : (id ?? null);
  const dockCompanyId = (createCompanyId ?? detailQ.data?.companyId) || undefined;

  return (
    <ModuleGuard moduleCode="crm_core">
      <DocumentPreviewDockHost
        enabled={previewDockEnabled && !!recordId}
        moduleId="internal"
        ownerType="document"
        ownerId={recordId}
        companyId={dockCompanyId}
      >
        <div className="space-y-4 p-6" data-testid="internal-doc-detail-page">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <IconButton
                onClick={() => navigate(withMenuItem('/internal-docs'))}
                data-testid="internal-doc-detail-back"
                aria-label={t('common.back')}
              >
                <ArrowLeft />
              </IconButton>
              <div className="flex items-start gap-3">
                <Shield className="mt-1 h-6 w-6 text-text-secondary" aria-hidden="true" />
                <div>
                  {isCreate ? (
                    <h1
                      className="text-2xl font-semibold text-text-primary"
                      data-testid="internal-doc-detail-create-title"
                    >
                      {t('tenant.documents.internal.detail.createTitle')}
                      <DocumentTypeCreateBadge
                        docClass="internal"
                        documentTypeId={effectiveDocumentTypeId}
                        companyId={createCompanyId}
                        testId="internal-doc-detail-create-type-badge"
                        onChange={(newTypeId, newCompanyId) => {
                          const params = new URLSearchParams(searchParams);
                          params.set('documentTypeId', newTypeId);
                          params.set('companyId', newCompanyId);
                          setSearchParams(params, { replace: true });
                          setForm((prev) => ({ ...prev, documentTypeId: newTypeId }));
                        }}
                      />
                    </h1>
                  ) : (
                    <>
                      <h1
                        className="text-2xl font-semibold text-text-primary"
                        data-testid="internal-doc-detail-title"
                      >
                        {form.systemNumber ??
                          form.title ??
                          t('tenant.documents.internal.detail.unnamedDoc')}
                        {canEdit && (
                          <DocumentTypeChangeBadge
                            docClass="internal"
                            documentTypeId={
                              pendingDocumentTypeId ?? detailQ.data?.documentTypeId ?? null
                            }
                            companyId={detailQ.data?.companyId ?? null}
                            testId="internal-doc-detail-change-type-badge"
                            disabled={!editing}
                            onConfirm={(newTypeId) => {
                              setPendingDocumentTypeId(newTypeId);
                              setLayoutOverrideId(null);
                            }}
                          />
                        )}
                        {pendingDocumentTypeId &&
                          pendingDocumentTypeId !== detailQ.data?.documentTypeId && (
                            <span
                              className="ml-2 rounded bg-accent-50 px-2 py-0.5 align-middle text-xs font-normal text-accent-700"
                              data-testid="internal-doc-detail-pending-type-number-hint"
                            >
                              {t('tenant.documents.typePicker.numberPendingOnSave')}
                            </span>
                          )}
                      </h1>
                      <div className="mt-1 flex items-center gap-3 text-xs text-text-tertiary">
                        {form.status && (
                          <span className="rounded bg-accent-50 px-2 py-0.5 text-accent-700">
                            {standardDocumentStatusLabel(form.status, t)}
                          </span>
                        )}
                        {form.documentDate && <span>{form.documentDate}</span>}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <DocumentLayoutPicker
                moduleSlug="internal"
                value={layoutOverrideId}
                onChange={setLayoutOverrideId}
                myRoleId={myRoleId}
                isSuperUser={isSuperUser}
              />
              {editing ? (
                <>
                  <Button
                    onClick={handleCancel}
                    variant="secondary"
                    leftIcon={<XIcon size={14} aria-hidden="true" />}
                    disabled={saveMutation.isPending}
                    data-testid="internal-doc-detail-cancel-btn"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={handleSave}
                    variant="primary"
                    leftIcon={
                      isCreate ? (
                        <Plus size={14} aria-hidden="true" />
                      ) : (
                        <Save size={14} aria-hidden="true" />
                      )
                    }
                    loading={saveMutation.isPending}
                    disabled={saveMutation.isPending}
                    data-testid="internal-doc-detail-save-btn"
                  >
                    {isCreate ? t('common.create') : t('common.save')}
                  </Button>
                </>
              ) : detailQ.data?.gcrecord ? (
                canDelete && (
                  <Button
                    onClick={() => setConfirmRestore(true)}
                    variant="primary"
                    leftIcon={<RotateCcw size={14} aria-hidden="true" />}
                    loading={restoreMutation.isPending}
                    disabled={restoreMutation.isPending}
                    data-testid="internal-doc-detail-restore-btn"
                  >
                    {t('tenant.documents.internal.actions.restore')}
                  </Button>
                )
              ) : (
                <>
                  {canDelete && (
                    <Button
                      onClick={() => setConfirmDelete(true)}
                      variant="danger"
                      disabled={sqlButton.recordLocked}
                      leftIcon={<Trash2 size={14} aria-hidden="true" />}
                      data-testid="internal-doc-detail-delete-btn"
                    >
                      {t('common.delete')}
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      onClick={() => setEditing(true)}
                      disabled={sqlButton.recordLocked}
                      leftIcon={<Pencil size={14} aria-hidden="true" />}
                      data-testid="internal-doc-detail-edit-btn"
                    >
                      {t('common.edit')}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Zadanie przycisku SQL na dokumencie (w kolejce lub w toku): dokumentu nie da się zapisać ani
            usunąć, „Edytuj” i „Usuń” są nieaktywne; pasek znika po zakończeniu zadania. */}
          {sqlButton.lockBanner}

          {/* Process approval banner */}
          {!isCreate && id && processesActive ? (
            <ProcessApprovalBanner
              moduleCode={DOCUMENTS_MODULE_CODE}
              recordId={id}
              documentTypeId={detailQ.data?.documentTypeId ?? null}
              beforeDecision={saveBeforeDecision.beforeDecision}
            />
          ) : null}

          {/* Silnik formatek (E4, mirror CommercialDocDetail): jedna ścieżka dla
          create/edit/view — 'data' NIE ma już tabRenderers override, renderer
          schodzi rekurencyjnie w children zakładki 'data' (topUpDocumentLayout
          ją syntetyzuje/dosypuje) i woła `renderField(fieldId)` per pole.
          Pole dodatkowe pokazuje się tylko tam, gdzie ktoś postawił je w układzie
          (top-up niczego nie dokleja). Tabele dodatkowe (additional_view/satellite_view) dostępne
          od razu w trybie buforowanym (bez HTTP, delty jadą w POST/PATCH —
          patrz saveAggregate). Zakładki „Pola dodatkowe" już nie ma; 'audit_log' to
          przenośny blok (internal_audit_view) z komunikatem „zapisz najpierw" do
          czasu zapisu rekordu. Stary blok ext_fields_view w zamrożonym układzie
          renderuje się jako nic (RecursiveFormRenderer — typ poza PLACEHOLDER_TYPES i rejestrem). */}
          <RecursiveFormRenderer
            layoutConfig={engineLayout}
            activeTabId={tab}
            onTabChange={setTab}
            myRoleId={myRoleId}
            isSuperUser={isSuperUser}
            renderField={renderField}
            contextRenderers={{
              // dokument-blokify (2026-08-24): przenośny blok „Audit log" — mirror kartotek. Guard
              // `!isCreate && recordId` (w trybie create `id==='new'`). Samodzielnej zakładki
              // „Pola dodatkowe" już nie ma: pole dodatkowe pokazuje się tylko tam, gdzie ktoś
              // postawił je w układzie, a jego wartość jest czytana z odpowiedzi GET rekordu.
              internal_audit_view: () =>
                !isCreate && recordId ? (
                  <SystemModuleAuditTab
                    moduleSlug="internal"
                    recordId={recordId}
                    testIdPrefix="internal-audit"
                  />
                ) : (
                  <SaveFirstNotice
                    message={t('tenant.documents.internal.detail.tabDisabled.save')}
                  />
                ),
              ...buildDocumentAdditionalRenderers(recordId, {
                editable: editing,
                buffered: true,
                processesActive,
                beforeDecision: saveBeforeDecision.beforeDecision,
                documentTypeId: detailQ.data?.documentTypeId ?? null,
                onDeltasChange: handleAdditionalDeltasChange,
                getAddRowDraft,
                onAddRowDraftChange: handleAddRowDraftChange,
                // Data-loss bugfix (mirror ContractDetail/SaleInvoiceDetail): pass
                // the hook's `getBufferedValue` (recreated on every Map change)
                // into this un-memoized factory call (it runs fresh every render),
                // so a Tabs-driven remount of AdditionalTableTab reads the intact
                // CURRENT buffer instead of an empty one. See AdditionalTableTab
                // header + useAdditionalTableDeltas.ts.
                getBufferedValue,
                companyId: createCompanyId ?? detailQ.data?.companyId ?? null,
                // F22 (widoki dodatkowe) — recordValues dla wiązania `record_field` oraz wartości
                // rodzica dla `parent_field`: ext (żywe, działa w create) + pola nagłówka
                // dokumentu przez config silnika klasy gołej.
                extValues,
                coreRecordValues: buildCoreFieldRecordValues(internalEngineConfig, form),
                // Klucz wiązań `parent_field` w tabelach dodatkowych tej klasy dokumentu.
                parentKey: 'internal',
              }),
              // Klocek „Przycisk SQL": zadanie w tle na tym dokumencie. Prawo = ten sam sygnał co „Edytuj”
              // (usunięty dokument nie ma „Edytuj”, więc i przycisk jest wyszarzony); serwer i tak sprawdza
              // prawo edycji z typem i etapem procesu.
              sql_button: sqlButton.renderSqlButton({
                canEdit: canEdit && !detailQ.data?.gcrecord,
                savePending: saveMutation.isPending,
              }),
              attachments_view: () =>
                recordId ? (
                  <AttachmentsSection
                    ownerType="document"
                    ownerId={recordId}
                    companyId={(createCompanyId ?? detailQ.data?.companyId) || undefined}
                  />
                ) : null,
              ...buildDocumentPreviewContextRenderer({
                ownerType: 'document',
                ownerId: recordId,
                companyId: (createCompanyId ?? detailQ.data?.companyId) || undefined,
                extValues,
              }),
              document_items_view: ({ element }) =>
                editing ? (
                  <DocumentItemsEditor
                    value={itemsDraft}
                    onChange={handleItemsChange}
                    currencyCode={detailQ.data?.currencyCode}
                    companyId={createCompanyId ?? detailQ.data?.companyId ?? null}
                    invalidRowKeys={invalidItemRowKeys}
                    columnSettings={element.settings}
                  />
                ) : (
                  <DocumentItemsSection
                    items={detailQ.data?.items}
                    vatBreakdown={detailQ.data?.vatBreakdown}
                    amountNet={detailQ.data?.amountNet}
                    amountVat={detailQ.data?.amountVat}
                    amountGross={detailQ.data?.amountGross}
                    currencyCode={detailQ.data?.currencyCode}
                    columnSettings={element.settings}
                  />
                ),
            }}
          />

          {/* Delete confirm */}
          <ConfirmDialog
            open={confirmDelete}
            title={t('common.confirmDelete')}
            message={t('tenant.documents.internal.detail.deleteConfirm', {
              title: form.systemNumber ?? form.title ?? id,
            })}
            confirmLabel={t('common.delete')}
            variant="danger"
            onConfirm={() => {
              setConfirmDelete(false);
              deleteMutation.mutate({});
            }}
            onClose={() => setConfirmDelete(false)}
          />
          {controlWarning.dialog}
          {deleteControlWarning.dialog}

          {/* Restore confirm */}
          <ConfirmDialog
            open={confirmRestore}
            title={t('tenant.documents.internal.detail.restoreConfirmTitle', {
              title: form.systemNumber ?? form.title ?? id,
            })}
            message={t('tenant.documents.internal.detail.restoreConfirmBody')}
            confirmLabel={t('tenant.documents.internal.actions.restore')}
            variant="primary"
            onConfirm={() => {
              setConfirmRestore(false);
              restoreMutation.mutate();
            }}
            onClose={() => setConfirmRestore(false)}
          />
        </div>
      </DocumentPreviewDockHost>
    </ModuleGuard>
  );
}
